import React from 'react';
import AnimatedNumber from './AnimatedNumber';
import { formatCount, formatCurrency, formatPercent, netCents, netSalesCount } from './salesUtils';
import { REFUND_TEXT } from './theme';

// `sub` is a plain string (truncated, full text in the title). `subNode` replaces it
// with richer content that may wrap (refund breakdowns), titled by `sub`.
// The {' '} separators keep textContent readable ("Sales 3 2 refunded"); flexbox
// drops whitespace-only children so they do not affect layout.
const Kpi = ({ label, testId, children, sub, subNode, accent, className = '' }) => (
  <div className={`sales-card sales-kpi ${accent ? 'sales-kpi-accent sales-card-lit' : ''} ${className}`} data-testid={testId}>
    <span className="sales-label">{label}</span>{' '}
    <span className="sales-kpi-value sales-num">{children}</span>{' '}
    {subNode ? (
      <span className="sales-kpi-sub whitespace-normal sales-num" title={sub}>{subNode}</span>
    ) : (
      sub && <span className="sales-kpi-sub" title={sub}>{sub}</span>
    )}
  </div>
);

// Shared with the loading skeleton so tiles never jump when data lands.
// md: 3+3 then 2+2+2 on a 6-col grid (no orphan tile); xl: one row of five.
export const KPI_GRID = 'grid grid-cols-2 md:grid-cols-6 xl:grid-cols-5 gap-3';
export const KPI_SPANS = [
  'md:col-span-3 xl:col-span-1',
  'md:col-span-3 xl:col-span-1',
  'md:col-span-2 xl:col-span-1',
  'md:col-span-2 xl:col-span-1',
  'col-span-2 md:col-span-2 xl:col-span-1',
];

// Five headline numbers for the selected range. Revenue and Sales are NET of refunds
// (refunds count against the bucket of the original sale); when anything was refunded
// the sub-line shows the gross / refunded breakdown instead of the range label.
// Percentages are server-computed (net) against ALL sales in the same range + currency.
const KpiCards = ({ range, currency, rangeLabel }) => {
  const money = (v) => formatCurrency(Math.round(v), currency);
  const refunded = range.refunded_cents || 0;
  const refundCount = range.refund_count || 0;
  const grossText = `Gross ${formatCurrency(range.gross_cents, currency)},`;
  const refundsText = `refunds ${formatCurrency(refunded, currency)}`;
  const totalNet = range.total_net_cents ?? range.total_gross_cents;
  const totalNetCount = range.total_net_sales_count ?? range.total_sales_count;
  return (
    <div className={KPI_GRID} data-testid="kpi-cards" aria-label={`Sales figures, ${rangeLabel}`}>
      <Kpi
        label="Sales"
        testId="kpi-sales-count"
        sub={refundCount > 0 ? `${formatCount(refundCount)} refunded, ${rangeLabel}` : rangeLabel}
        subNode={refundCount > 0 ? <span className={REFUND_TEXT}>{formatCount(refundCount)} refunded</span> : null}
        className={KPI_SPANS[0]}
      >
        <AnimatedNumber value={netSalesCount(range)} format={(v) => formatCount(Math.round(v))} />
      </Kpi>
      <Kpi
        label="Revenue"
        testId="kpi-revenue"
        sub={refunded > 0 ? `${grossText} ${refundsText}` : rangeLabel}
        subNode={refunded > 0 ? (
          <span className="inline-flex flex-wrap gap-x-1">
            <span>{grossText}</span>{' '}
            <span className={REFUND_TEXT}>{refundsText}</span>
          </span>
        ) : null}
        className={KPI_SPANS[1]}
      >
        <AnimatedNumber value={netCents(range)} format={money} />
      </Kpi>
      <Kpi label="% of total sales" testId="kpi-pct-sales" accent sub={`of ${formatCount(totalNetCount)} sales`} className={KPI_SPANS[2]}>
        <AnimatedNumber value={range.pct_of_sales_count || 0} format={formatPercent} />
      </Kpi>
      <Kpi label="% of total revenue" testId="kpi-pct-revenue" accent sub={`of ${formatCurrency(totalNet, currency)}`} className={KPI_SPANS[3]}>
        <AnimatedNumber value={range.pct_of_revenue || 0} format={formatPercent} />
      </Kpi>
      <Kpi label="Rank by revenue" testId="kpi-rank" sub={rangeLabel} className={KPI_SPANS[4]}>
        {range.rank ? `#${range.rank} of ${range.ranked_videos}` : 'Unranked'}
      </Kpi>
    </div>
  );
};

export default KpiCards;
